import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/AuthLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import config from '../../config';

const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Введите адрес электронной почты';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Введите корректный адрес электронной почты';
    }

    if (!formData?.password) {
      newErrors.password = 'Введите пароль';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (error) {
      let message = 'Ошибка входа. Попробуйте снова.';
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = 'Неверный email или пароль';
      } else if (error.code === 'auth/invalid-credential') {
        message = 'Неверный email или пароль';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Слишком много попыток. Попробуйте позже.';
      }
      setErrors({ submit: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/register');
  };

  const handleCreateAccount = () => {
    navigate('/register');
  };

  return (
    <AuthLayout
      title={`Вход в ${config.appName}`}
      subtitle="Управляйте своими арендными площадками"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          type="email"
          name="email"
          label="Электронная почта"
          placeholder="example@rentalspace.ru"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Пароль"
            placeholder="Введите ваш пароль"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-text-secondary hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            disabled={isLoading}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        {errors?.submit && (
          <div className="flex items-start gap-2 p-4 bg-error/10 border border-error/20 rounded-lg">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{errors?.submit}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isLoading}
          iconName="LogIn"
          iconPosition="left"
        >
          {isLoading ? 'Вход...' : 'Войти'}
        </Button>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-secondary transition-colors font-medium"
            disabled={isLoading}
          >
            Забыли пароль?
          </button>
          <button
            type="button"
            onClick={handleCreateAccount}
            className="text-sm text-text-secondary hover:text-foreground transition-colors"
            disabled={isLoading}
          >
            Создать аккаунт
          </button>
        </div>
      </form>
      <div className="mt-8 pt-6 border-t border-border">
        <div className="flex items-center justify-center gap-2 text-text-secondary">
          <Icon name="Shield" size={16} />
          <p className="caption">Защищенное соединение</p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;